/**
 * @file        src/modals/RoomModal.jsx
 * @description Room details modal — gallery + amenities for the selected room.
 *              Ported from `#roomModal` (~line 1889) in the design HTML.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-07-29 Claude — Bigger popup: max-w-3xl → max-w-5xl (768 → 1024px) and
 *                          max-h 88vh → 92vh. The detail column went 280 → 360px with
 *                          it — at 280px the amenity labels ("WiFi speed - 25+ Mbps")
 *                          wrapped mid-phrase, and widening only the modal would have
 *                          spent every new pixel on the image and left them wrapping.
 *   - 2026-07-24 Claude — "Select Room": teal→green gradient → brand black (#1C1C1C)
 *                          with white text.
 *   - 2026-06-22 Claude — Initial port. Replaced the imperative
 *                         `roomModalBody` innerHTML build with JSX bound to
 *                         the booking store's `hotel` + the `room` prop.
 *   - 2026-06-23 Claude — Index-based gallery: prev/next arrow buttons + an
 *                         image counter on the main image; main image uses a
 *                         4:3 aspect ratio instead of a fixed width-heavy box.
 */
import { useEffect, useState } from 'react';
import { useBooking } from '../store/booking.store.js';
import { inr } from '../lib/format.js';
import { NO_IMAGE } from '../lib/placeholder.js';

export default function RoomModal({ open, onClose, payload }) {
  const hotel = useBooking((s) => s.hotel);
  const room = payload?.room || useBooking.getState().room;
  // Prefer this room's own images; fall back to the hotel gallery.
  const gallery = (room?.gallery?.length ? room.gallery : hotel?.gallery) || [];
  const [idx, setIdx] = useState(0);
  const [showAllAmen, setShowAllAmen] = useState(false);

  useEffect(() => { setIdx(0); setShowAllAmen(false); }, [room, hotel]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || !room) return null;

  const len = gallery.length;
  const mainImg = gallery[idx] || NO_IMAGE;
  const go = (d) => { if (len) setIdx((i) => (i + d + len) % len); };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-5xl max-h-[92svh] overflow-y-auto rounded-3xl border border-line bg-panel no-scrollbar">
        <div className="flex items-center justify-between px-5 py-3 border-b border-line sticky top-0 bg-panel z-10">
          <button onClick={onClose} className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink cursor-pointer">
            <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="M11 5 4 12l7 7 1.4-1.4L7.8 13H20v-2H7.8l4.6-4.6z" /></svg>
            Back to Album
          </button>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_360px]">
          <div className="p-4">
            <div className="group relative overflow-hidden rounded-2xl ring-1 ring-line shadow-2xl shadow-black/40">
              <img src={mainImg} alt="" className="w-full aspect-[4/3] object-cover bg-night transition duration-500 group-hover:scale-[1.03]" />
              {/* subtle gradient so the counter stays legible */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-night/80 to-transparent" />
              {len > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => go(-1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-night/60 border border-ink/10 backdrop-blur flex items-center justify-center text-ink opacity-0 group-hover:opacity-100 hover:bg-neon hover:text-onaccent cursor-pointer transition"
                    aria-label="Previous image"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M15 6 9 12l6 6z" /></svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => go(1)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-night/60 border border-ink/10 backdrop-blur flex items-center justify-center text-ink opacity-0 group-hover:opacity-100 hover:bg-neon hover:text-onaccent cursor-pointer transition"
                    aria-label="Next image"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M9 6l6 6-6 6z" /></svg>
                  </button>
                  <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-night/70 backdrop-blur border border-ink/10 text-[11px] font-semibold text-ink">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-neon"><path fill="currentColor" d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2M8.5 13.5l2.5 3 3.5-4.5 4.5 6H5z" /></svg>
                    {idx + 1} / {len}
                  </span>
                </>
              )}
            </div>
            {len > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar pb-1">
                {gallery.map((g, gi) => (
                  <button
                    key={gi}
                    type="button"
                    onClick={() => setIdx(gi)}
                    className={`relative shrink-0 rounded-xl overflow-hidden transition ${idx === gi ? 'ring-2 ring-neon' : 'ring-1 ring-line opacity-60 hover:opacity-100'}`}
                  >
                    <img src={g} alt="" className="w-20 h-14 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-5 border-l border-line">
            <h3 className="font-display text-xl font-extrabold">{room.name}</h3>
            {(room.beds || room.sleeps) && (
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                {room.beds && (
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-night/50 px-2.5 py-1.5">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-neon"><path fill="currentColor" d="M21 10V8a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v2a2 2 0 0 0-1 1.7V19h2v-2h16v2h2v-7.3A2 2 0 0 0 21 10M6 7h12a1 1 0 0 1 1 1v2H5V8a1 1 0 0 1 1-1"/></svg>
                    {room.beds}
                  </span>
                )}
                {room.sleeps ? (
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-night/50 px-2.5 py-1.5">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-neon"><path fill="currentColor" d="M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4m-8 0a4 4 0 1 0-4-4 4 4 0 0 0 4 4m0 2c-2.7 0-8 1.3-8 4v3h10v-3c0-1 .4-1.8 1-2.5A13 13 0 0 0 8 13m8 0c-.3 0-.7 0-1.1.1A5 5 0 0 1 18 17v3h6v-3c0-2.7-5.3-4-8-4"/></svg>
                    Sleeps {room.sleeps}
                  </span>
                ) : null}
              </div>
            )}
            {(room.amen || []).length > 0 && (
              <>
                <p className="font-semibold text-sm mt-4 mb-2">Room amenities</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                  {(showAllAmen ? room.amen : room.amen.slice(0, 6)).map((a, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-muted">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-neon shrink-0"><path fill="currentColor" d="m9 16.2-3.5-3.5L4 14.2 9 19l11-11-1.4-1.4z" /></svg>
                      {a}
                    </span>
                  ))}
                </div>
                {room.amen.length > 6 && (
                  <button
                    type="button"
                    onClick={() => setShowAllAmen((v) => !v)}
                    className="text-neon text-xs font-semibold mt-2.5 cursor-pointer hover:text-ink"
                  >
                    {showAllAmen ? 'Show less' : `+${room.amen.length - 6} more`}
                  </button>
                )}
              </>
            )}
            {(room.modal || []).map(([title, items], i) => (
              <div key={i}>
                <p className="font-semibold text-sm mt-4 mb-2">{title}</p>
                <div className="space-y-1.5 text-sm text-muted">
                  {items.map((it, j) => (
                    <p key={j} className="flex items-center gap-1.5">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-neon shrink-0"><path fill="currentColor" d="m9 16.2-3.5-3.5L4 14.2 9 19l11-11-1.4-1.4z" /></svg>
                      {it}
                    </p>
                  ))}
                </div>
              </div>
            ))}
            <button
              onClick={() => { payload?.onSelect?.(); onClose?.(); }}
              className="w-full mt-5 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold cursor-pointer hover:brightness-125 transition"
            >
              Select Room
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
